// FlowChart.jsx - Simplified version focusing on clean connections
import React, { useCallback, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import EventNode from './EventNode';
import ChoiceNode from './ChoiceNode';

const nodeTypes = {
  event: EventNode,
  choice: ChoiceNode,
};

export default function FlowChart({ 
  blocks, 
  activeBlockId,
  onNodePositionChange, 
  onBlockConnection 
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Convert blocks to ReactFlow nodes
  useEffect(() => {
    const flowNodes = blocks.map((block) => ({
      id: block.id,
      type: block.type || 'event',
      position: block.position || { x: 100, y: 100 },
      data: { 
        block, 
        activeBlockId,
      },
    }));
    setNodes(flowNodes);
  }, [blocks, activeBlockId, setNodes]);

  // Convert block connections to ReactFlow edges
  useEffect(() => {
    const flowEdges = [];
    
    blocks.forEach((block) => {
      if (block.type === 'event' && block.next) {
        // Event blocks → next connections
        block.next.forEach((targetId) => {
          const target = blocks.find(b => b.id === targetId);
          if (target) {
            flowEdges.push({
              id: `${block.id}-${targetId}`,
              source: block.id,
              target: targetId,
              type: 'smoothstep',
              style: { 
                stroke: '#6B7280', 
                strokeWidth: 2 
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#6B7280',
              },
            });
          }
        });
      } else if (block.type === 'choice' && block.choices) {
        // Choice blocks → choice connections
        block.choices.forEach((choice, idx) => {
          if (choice.targetId) {
            const target = blocks.find(b => b.id === choice.targetId);
            if (target) {
              flowEdges.push({
                id: `${block.id}-choice-${idx}`,
                source: block.id,
                sourceHandle: `choice-${idx}`,
                target: choice.targetId,
                type: 'smoothstep',
                label: choice.label,
                style: { 
                  stroke: '#059669', 
                  strokeWidth: 2 
                },
                markerEnd: {
                  type: 'arrowclosed',
                  color: '#059669',
                },
                labelStyle: {
                  fill: '#059669',
                  fontWeight: 500,
                  fontSize: 12,
                },
                labelBgPadding: [8, 4],
                labelBgBorderRadius: 4,
                labelBgStyle: { 
                  fill: '#FFFFFF', 
                  stroke: '#059669', 
                  strokeWidth: 1 
                },
              });
            }
          }
        });
      }
    });

    setEdges(flowEdges);
  }, [blocks, setEdges]);

  // Handle node position changes
  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    
    // Update block positions when nodes are dragged
    changes.forEach((change) => {
      if (change.type === 'position' && change.position) {
        onNodePositionChange(change.id, change.position);
      }
    });
  }, [onNodesChange, onNodePositionChange]);

  // Handle new connections
  const onConnect = useCallback((connection) => {
    // Find source and target blocks
    const sourceBlock = blocks.find(b => b.id === connection.source);
    const targetBlock = blocks.find(b => b.id === connection.target);
    
    if (sourceBlock && targetBlock) {
      onBlockConnection(
        connection.source, 
        connection.target, 
        connection.sourceHandle
      );
    }
  }, [blocks, onBlockConnection]);

  // Custom node color function for minimap
  const nodeColor = (node) => {
    if (node.data?.block?.id === activeBlockId) return '#3B82F6';
    if (node.type === 'choice') return '#059669';
    return '#6B7280';
  };

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        attributionPosition="bottom-left"
      >
        <MiniMap 
          nodeColor={nodeColor}
          pannable
          zoomable
        />
        <Controls />
        <Background color="#e5e7eb" gap={16} />
      </ReactFlow>
    </div>
  );
}